import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { StorageService } from 'src/app/services/auth/storage.service';
import { environment } from 'src/environments/environment';

interface ResponseB4A<T> {
  result: T;
}

export interface SessionKillerConfig {
  configId?: number;
  isEnabled?: boolean;
  maxMinutes?: number;
  isLoggingEnabled?: boolean;
  updatedAt?: string;
  updatedBy?: string | null;
}

export interface SessionKillerTarget {
  sessionId?: number;
  loginName?: string;
  hostName?: string;
  programName?: string;
  databaseName?: string;
  elapsedMinutes?: number;
  killReason?: string;
  sqlText?: string;
  evaluatedAtUtc?: string;
}

export interface SessionKillerLog {
  logId?: number;
  sessionId?: number;
  loginName?: string;
  hostName?: string;
  programName?: string;
  killReason?: string;
  sqlText?: string;
  databaseName?: string;
  elapsedMinutes?: number;
  killedAt?: string;
  wasDryRun?: boolean;
}

export interface SessionKillerWhitelist {
  whitelistId?: number;
  filterType?: string;
  filterValue?: string;
  isEnabled?: boolean;
  notes?: string | null;
  createdAt?: string;
  createdBy?: string | null;
}

export interface SessionKillerConfigUpdateRequest {
  isEnabled?: boolean;
  maxMinutes?: number;
  isLoggingEnabled?: boolean;
}

export interface SessionKillerWhitelistRequest {
  filterType?: string;
  filterValue?: string;
  isEnabled?: boolean;
  notes?: string | null;
}

@Injectable({ providedIn: 'root' })
export class SessionKillerService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/it-ops/session-killer`;

  getConfig(): Observable<SessionKillerConfig> {
    return this.http
      .get<ResponseB4A<SessionKillerConfig>>(`${this.baseUrl}/config`, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result));
  }

  updateConfig(
    body: SessionKillerConfigUpdateRequest
  ): Observable<SessionKillerConfig> {
    return this.http
      .put<ResponseB4A<SessionKillerConfig>>(`${this.baseUrl}/config`, body, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result));
  }

  dryRun(): Observable<SessionKillerTarget[]> {
    return this.http
      .get<ResponseB4A<SessionKillerTarget[]>>(`${this.baseUrl}/dry-run`, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result ?? []));
  }

  listLogs(): Observable<SessionKillerLog[]> {
    return this.http
      .get<ResponseB4A<SessionKillerLog[]>>(`${this.baseUrl}/logs`, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result ?? []));
  }

  listWhitelist(): Observable<SessionKillerWhitelist[]> {
    return this.http
      .get<ResponseB4A<SessionKillerWhitelist[]>>(
        `${this.baseUrl}/whitelist`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result ?? []));
  }

  addWhitelist(
    body: SessionKillerWhitelistRequest
  ): Observable<SessionKillerWhitelist> {
    return this.http
      .post<ResponseB4A<SessionKillerWhitelist>>(
        `${this.baseUrl}/whitelist`,
        body,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  updateWhitelist(
    whitelistId: number,
    body: SessionKillerWhitelistRequest
  ): Observable<SessionKillerWhitelist> {
    return this.http
      .put<ResponseB4A<SessionKillerWhitelist>>(
        `${this.baseUrl}/whitelist/${whitelistId}`,
        body,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  deleteWhitelist(whitelistId: number): Observable<string> {
    return this.http
      .delete<ResponseB4A<string>>(
        `${this.baseUrl}/whitelist/${whitelistId}`,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  private authHeaders(): HttpHeaders {
    const headers = new HttpHeaders();
    if (StorageService.userLogged && StorageService.token) {
      return headers.set('Authorization', `Bearer ${StorageService.token}`);
    }
    return headers;
  }
}
